import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import leaveService from '../../services/leaveService';
import LeaveRequestForm from './LeaveRequestForm';
import { useWebSocket } from '../../context/WebSocketContext';

import {
  CalendarDaysIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UsersIcon,
  EyeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const LeaveManagement = ({ user }) => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  
  // Manager team calendar state
  const [teamCalendar, setTeamCalendar] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamSummary, setTeamSummary] = useState({ totalRequests: 0, pending: 0, approved: 0, rejected: 0 });
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [requestsPagination, setRequestsPagination] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    page: 1,
    limit: 10
  });
  const [loading, setLoading] = useState(true);
  const [editingRequest, setEditingRequest] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [viewRequestLoading, setViewRequestLoading] = useState(false);

  // Bulk approval state
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkComments, setBulkComments] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // WebSocket context
  const { socket, isConnected } = useWebSocket();

  // Check user role permissions (check both role and Role formats for compatibility)
  const userRole = user?.Role?.name || user?.role?.name;
  const canManageAllRequests = ['super_admin', 'admin', 'hr'].includes(userRole);
  const canApproveRequests = ['super_admin', 'admin', 'hr', 'manager'].includes(userRole);
  const isManager = userRole === 'manager';
  const isEmployee = userRole === 'employee';
  
  console.log('🔍 User role detection:', { 
    userRole, 
    isManager, 
    'user?.Role?.name': user?.Role?.name,
    'user?.role?.name': user?.role?.name,
    'userRole === "manager"': userRole === 'manager',
    'typeof userRole': typeof userRole,
    'full user.Role': user?.Role 
  });

  // Check if user has HR/Admin permissions
  const canManageLeaveTypes = ['hr', 'super_admin', 'admin'].includes(userRole);

  useEffect(() => {
    loadDashboardData();
    // Load team calendar for managers
    if (isManager) {
      loadTeamCalendar();
    }
  }, [isManager]);

  useEffect(() => {
    if (currentView === 'requests') {
      loadAllRequests();
    }
  }, [currentView, filters]);

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    console.log('🔄 LeaveManagement WebSocket useEffect:', { socket: !!socket, isConnected });
    
    // TEMPORARILY DISABLE WEBSOCKET LISTENERS FOR DEBUGGING
    console.log('🔄 WebSocket listeners DISABLED for debugging');
    return;
    
    if (!socket || !isConnected) return;

    // Listen for leave updates
    const handleLeaveUpdate = (data) => {
      try {
        console.log('🔄 Real-time leave update received:', data);
        
        if (!data || typeof data !== 'object') {
          console.warn('Invalid leave update data:', data);
          return;
        }
        
        if (data.type === 'new_request') {
          toast.success('New leave request submitted');
          loadDashboardData(); // Refresh dashboard data
          if (currentView === 'requests') {
            loadAllRequests(); // Refresh requests list
          }
        } else if (data.type === 'status_change') {
          const status = data.data?.status || 'updated';
          toast.success(`Leave request ${status} successfully`);
          loadDashboardData(); // Refresh dashboard data
          if (currentView === 'requests') {
            loadAllRequests(); // Refresh requests list
          }
        } else if (data.type === 'bulk_status_change') {
          const action = data.data?.action || 'processed';
          const count = data.data?.processedCount || 0;
          toast.success(`Bulk ${action} completed: ${count} processed`);
          loadDashboardData(); // Refresh dashboard data
          if (currentView === 'requests') {
            loadAllRequests(); // Refresh requests list
          }
        }
      } catch (error) {
        console.error('Error handling leave update:', error);
      }
    };

    // Listen for leave management updates (for HR/Admin)
    const handleLeaveManagementUpdate = (data) => {
      try {
        if (canManageAllRequests) {
          console.log('🔄 Leave management update received:', data);
          loadDashboardData(); // Refresh dashboard data
          if (currentView === 'requests') {
            loadAllRequests(); // Refresh requests list
          }
        }
      } catch (error) {
        console.error('Error handling leave management update:', error);
      }
    };

    socket.on('leave_update', handleLeaveUpdate);
    socket.on('leave_management_update', handleLeaveManagementUpdate);

    return () => {
      try {
        socket.off('leave_update', handleLeaveUpdate);
        socket.off('leave_management_update', handleLeaveManagementUpdate);
      } catch (error) {
        console.error('Error cleaning up WebSocket listeners:', error);
      }
    };
  }, [socket, isConnected, currentView, canManageAllRequests]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load leave balance
      const balanceResponse = await leaveService.getLeaveBalance();
      if (balanceResponse.success) {
        setLeaveBalance(balanceResponse.data || []);
      }

      // Load recent requests (limit to 5)
      const requestsResponse = await leaveService.getLeaveRequests({ 
        limit: 5,
        sort: 'createdAt',
        order: 'DESC'
      });
      if (requestsResponse.success) {
        setRecentRequests(requestsResponse.data.leaveRequests || []);
      }
    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const loadAllRequests = async (filterParams = {}) => {
    try {
      setRequestsLoading(true);
      
      const params = {
        ...filters,
        ...filterParams
      };

      // If HR/Admin, don't filter by userId to see all requests
      // If employee, the backend will automatically filter to their requests
      const response = await leaveService.getLeaveRequests(params);
      if (response.success) {
        setAllRequests(response.data.leaveRequests || []);
        setRequestsPagination(response.data.pagination || {});
      }
    } catch (error) {
      console.error('❌ Error loading all requests:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadTeamCalendar = async (year = new Date().getFullYear(), month = null) => {
    try {
      setCalendarLoading(true);
      
      const response = await leaveService.getTeamCalendar(year, month);
      if (response.success) {
        setTeamCalendar(response.data.leaveCalendar || []);
        setTeamMembers(response.data.teamMembers || []);
        setTeamSummary(response.data.summary || { totalRequests: 0, pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('❌ Error loading team calendar:', error);
      toast.error('Failed to load team calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  // HR Management Functions
  const approveLeaveRequest = async (requestId, approvalType = 'manager') => {
    try {
      const response = await leaveService.processLeaveRequest(requestId, {
        action: 'approve',
        approvalType,
        comments: 'Approved by HR Manager'
      });
      
      if (response.success) {
        toast.success('Leave request approved successfully');
        loadAllRequests();
      }
    } catch (error) {
      console.error('Error approving leave request:', error);
      toast.error(error.response?.data?.error || 'Failed to approve leave request');
    }
  };

  const rejectLeaveRequest = async (requestId, reason = '') => {
    try {
      const response = await leaveService.processLeaveRequest(requestId, {
        action: 'reject',
        comments: reason
      });
      if (response.success) {
        toast.success('Leave request rejected successfully');
        loadAllRequests();
      }
    } catch (error) {
      console.error('Error rejecting leave request:', error);
      toast.error(error.response?.data?.error || 'Failed to reject leave request');
    }
  };

  const bulkProcessLeaveRequests = async () => {
    if (!bulkAction || selectedRequests.length === 0) {
      toast.error('Please select requests and an action');
      return;
    }

    try {
      setBulkProcessing(true);
      const response = await leaveService.bulkProcessLeaveRequests(
        selectedRequests,
        bulkAction,
        bulkComments
      );
      
      if (response.success) {
        toast.success(`Bulk ${bulkAction} completed: ${response.data.processed} processed`);
        setSelectedRequests([]);
        setBulkAction('');
        setBulkComments('');
        loadAllRequests();
      }
    } catch (error) {
      console.error('Error bulk processing leave requests:', error);
      toast.error(error.response?.data?.error || 'Failed to process leave requests');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleRequestSelection = (requestId) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const handleSelectAll = () => {
    const pendingRequests = allRequests.filter(req => 
      req.status === 'pending' || req.status === 'approved_by_manager'
    );
    setSelectedRequests(pendingRequests.map(req => req.id));
  };

  const handleClearSelection = () => {
    setSelectedRequests([]);
  };

  const loadFullRequestDetails = async () => {
    try {
      setViewRequestLoading(true);
      
      const response = await leaveService.getLeaveRequest(viewingRequest.id);
      if (response.success) {
        setViewingRequest(response.data.leaveRequest);
      }
    } catch (error) {
      console.error('❌ Error loading full request details:', error);
      toast.error('Failed to load request details');
    } finally {
      setViewRequestLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'text-yellow-600 bg-yellow-100',
      'approved_by_manager': 'text-blue-600 bg-blue-100',
      'approved_by_hr': 'text-green-600 bg-green-100',
      'approved': 'text-green-600 bg-green-100',
      'rejected': 'text-red-600 bg-red-100',
      'cancelled': 'text-gray-600 bg-gray-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'pending': 'Pending Manager',
      'approved_by_manager': 'Pending HR',
      'approved_by_hr': 'Approved',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled'
    };
    return statusMap[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderTeamCalendar = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Team Leaves</h1>
          <p className="mt-1 text-sm text-gray-600">
            Calendar view of your team's leave requests and schedules
          </p>
        </div>
        <button
          onClick={() => setCurrentView('dashboard')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UsersIcon className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Team Members</dt>
                  <dd className="text-lg font-medium text-gray-900">{teamMembers.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                  <dd className="text-lg font-medium text-yellow-600">{teamSummary.pending}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Approved</dt>
                  <dd className="text-lg font-medium text-green-600">{teamSummary.approved}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Rejected</dt>
                  <dd className="text-lg font-medium text-red-600">{teamSummary.rejected}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Members and Leave List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Team Members */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
            </div>
            <div className="px-6 py-4">
              {teamMembers.length === 0 ? (
                <p className="text-gray-500">No team members found</p>
              ) : (
                <ul className="space-y-3">
                  {teamMembers.map((member) => (
                    <li key={member.id} className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                          <span className="text-sm font-medium text-white">
                            {member.firstName?.[0]}{member.lastName?.[0]}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {member.Employee?.employeeNumber || 'N/A'}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Leave Calendar List */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Recent Leave Requests</h3>
            </div>
            <div className="px-6 py-4">
              {calendarLoading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : teamCalendar.length === 0 ? (
                <p className="text-gray-500">No leave requests found</p>
              ) : (
                <ul className="space-y-4">
                  {teamCalendar.slice(0, 10).map((request) => (
                    <li key={request.id} className="border-l-4 pl-4 py-2" style={{ borderLeftColor: request.LeaveType?.color || '#3B82F6' }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {request.User?.firstName} {request.User?.lastName}
                          </p>
                          <p className="text-sm text-gray-600">
                            {request.LeaveType?.name} • {formatDate(request.startDate)} - {formatDate(request.endDate)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {request.totalDays} days • {request.reason}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                            {formatStatus(request.status)}
                          </span>
                          {request.status === 'pending' && (
                            <button
                              onClick={() => {
                                setViewingRequest(request);
                                setCurrentView('view-request');
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Review
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (currentView) {
      case 'new-request':
        return (
          <LeaveRequestForm
            onSubmit={async (data) => {
              try {
                const response = await leaveService.createLeaveRequest(data);
                if (response.success) {
                  toast.success('Leave request submitted successfully');
                  setCurrentView('dashboard');
                  loadDashboardData();
                }
              } catch (error) {
                console.error('Error submitting leave request:', error);
                toast.error(error.response?.data?.error || 'Failed to submit leave request');
              }
            }}
            onCancel={() => setCurrentView('dashboard')}
          />
        );
      
      case 'requests':
        return renderAllRequests();
      
      case 'edit-request':
        return (
          <LeaveRequestForm
            editMode={true}
            initialData={editingRequest}
            onSubmit={async (data) => {
              try {
                const response = await leaveService.updateLeaveRequest(editingRequest.id, data);
                if (response.success) {
                  toast.success('Leave request updated successfully');
                  setCurrentView('requests');
                  setEditingRequest(null);
                  loadAllRequests();
                }
              } catch (error) {
                console.error('Error updating leave request:', error);
                toast.error(error.response?.data?.error || 'Failed to update leave request');
              }
            }}
            onCancel={() => {
              setCurrentView('requests');
              setEditingRequest(null);
            }}
          />
        );
      
      case 'view-request':
        return renderViewRequest();
      
      case 'team-calendar':
        return renderTeamCalendar();
      
      default:
        return renderDashboard();
    }
  };

  const renderAllRequests = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {canManageAllRequests ? 'All Employee Leave Requests' : 'My Leave Requests'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {canManageAllRequests 
              ? 'View and manage all employee leave requests' 
              : 'View and manage your leave requests'
            }
          </p>
        </div>
        <button
          onClick={() => setCurrentView('dashboard')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          ← Back to Dashboard
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending Manager</option>
              <option value="approved_by_manager">Pending HR</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex-1 min-w-0">
            <label htmlFor="limit-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Per Page
            </label>
            <select
              id="limit-filter"
              value={filters.limit}
              onChange={(e) => setFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions (HR/Admin only) */}
      {canManageAllRequests && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Bulk Actions</h3>
            <div className="flex space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All Pending
              </button>
              <button
                onClick={handleClearSelection}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear Selection
              </button>
            </div>
          </div>
          
          {selectedRequests.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {selectedRequests.length} request(s) selected
                </span>
                <select
                  value={bulkAction}
                  onChange={(e) => setBulkAction(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select Action</option>
                  <option value="approve">Approve</option>
                  <option value="reject">Reject</option>
                </select>
                <input
                  type="text"
                  placeholder="Comments (optional)"
                  value={bulkComments}
                  onChange={(e) => setBulkComments(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  onClick={bulkProcessLeaveRequests}
                  disabled={!bulkAction || bulkProcessing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkProcessing ? 'Processing...' : 'Process Selected'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Requests List */}
      {requestsLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : allRequests.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No leave requests found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {filters.status ? 'No requests match the selected filters.' : 'You haven\'t submitted any leave requests yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <div className="flow-root">
              <ul className="-my-5 divide-y divide-gray-200">
                {allRequests.map((request) => (
                  <li key={request.id} className="py-5">
                    <div className="relative focus-within:ring-2 focus-within:ring-blue-500">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start space-x-3">
                          {/* Bulk selection checkbox (HR/Admin only) */}
                          {canManageAllRequests && (request.status === 'pending' || request.status === 'approved_by_manager') && (
                            <div className="flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={selectedRequests.includes(request.id)}
                                onChange={() => handleRequestSelection(request.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                            </div>
                          )}
                          <div className="flex-shrink-0">
                            <div 
                              className="w-3 h-3 rounded-full mt-2"
                              style={{ backgroundColor: request.LeaveType?.color || '#3B82F6' }}
                            ></div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-medium text-gray-900">
                                {request.LeaveType?.name}
                              </h3>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {formatStatus(request.status)}
                              </span>
                            </div>
                            <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <CalendarDaysIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                {formatDate(request.startDate)} - {formatDate(request.endDate)}
                              </div>
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                {request.totalDays} days
                              </div>
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <DocumentTextIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                Submitted {formatDate(request.createdAt)}
                              </div>
                            </div>
                            {request.reason && (
                              <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                                {request.reason}
                              </p>
                            )}
                            {request.isEmergency && (
                              <div className="mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Emergency Request
                              </div>
                            )}
                          </div>
                        </div>
                                                 <div className="flex-shrink-0 text-right space-x-2">
                           <button 
                             onClick={async () => {
                               setViewRequestLoading(true);
                               try {
                                 const response = await leaveService.getLeaveRequest(request.id);
                                 if (response.success) {
                                   setViewingRequest(response.data.leaveRequest);
                                   setCurrentView('view-request');
                                 }
                               } catch (error) {
                                 console.error('Error loading request details:', error);
                                 toast.error('Failed to load request details');
                               } finally {
                                 setViewRequestLoading(false);
                               }
                             }}
                             className="text-sm text-gray-600 hover:text-gray-800 font-medium focus:outline-none focus:underline"
                           >
                             View
                           </button>
                           
                           {/* HR Management Buttons */}
                           {canManageAllRequests && request.status === 'pending' && (
                             <>
                               <button 
                                 onClick={() => approveLeaveRequest(request.id, 'hr')}
                                 className="text-sm text-green-600 hover:text-green-800 font-medium focus:outline-none focus:underline"
                               >
                                 Approve
                               </button>
                               <button 
                                 onClick={() => rejectLeaveRequest(request.id)}
                                 className="text-sm text-red-600 hover:text-red-800 font-medium focus:outline-none focus:underline"
                               >
                                 Reject
                               </button>
                             </>
                           )}
                           
                           {/* Employee Edit Button */}
                           {!canManageAllRequests && request.status === 'pending' && (
                             <button 
                               onClick={() => {
                                 setEditingRequest(request);
                                 setCurrentView('edit-request');
                               }}
                               className="text-sm text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:underline"
                             >
                               Edit
                             </button>
                           )}
                         </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Pagination */}
          {requestsPagination.totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={!requestsPagination.hasPrevPage}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, page: Math.min(requestsPagination.totalPages, prev.page + 1) }))}
                    disabled={!requestsPagination.hasNextPage}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {((requestsPagination.currentPage - 1) * requestsPagination.itemsPerPage) + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(requestsPagination.currentPage * requestsPagination.itemsPerPage, requestsPagination.totalItems)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{requestsPagination.totalItems}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={!requestsPagination.hasPrevPage}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, requestsPagination.totalPages) }, (_, i) => {
                        const pageNumber = Math.max(1, requestsPagination.currentPage - 2) + i;
                        if (pageNumber <= requestsPagination.totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setFilters(prev => ({ ...prev, page: pageNumber }))}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                pageNumber === requestsPagination.currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, page: Math.min(requestsPagination.totalPages, prev.page + 1) }))}
                        disabled={!requestsPagination.hasNextPage}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderViewRequest = () => {
    if (!viewingRequest) return null;

    if (viewRequestLoading) {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Leave Request Details</h1>
              <p className="mt-1 text-sm text-gray-600">Loading...</p>
            </div>
            <button
              onClick={() => {
                setCurrentView('requests');
                setViewingRequest(null);
              }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              ← Back to Requests
            </button>
          </div>
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      );
    }

    const formatDateTime = (dateString) => {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getStatusIcon = (status) => {
      const icons = {
        'pending': '⏳',
        'approved_by_manager': '✅',
        'approved_by_hr': '✅',
        'approved': '✅',
        'rejected': '❌',
        'cancelled': '🚫'
      };
      return icons[status] || '❓';
    };

    const getStatusDescription = (status) => {
      const descriptions = {
        'pending': 'Awaiting manager approval',
        'approved_by_manager': 'Approved by manager, awaiting HR approval',
        'approved_by_hr': 'Approved by HR',
        'approved': 'Fully approved',
        'rejected': 'Request rejected',
        'cancelled': 'Request cancelled'
      };
      return descriptions[status] || status;
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Leave Request Details</h1>
            <p className="mt-1 text-sm text-gray-600">
              Detailed view of leave request #{viewingRequest.id}
            </p>
          </div>
          <button
            onClick={() => {
              setCurrentView('requests');
              setViewingRequest(null);
            }}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            ← Back to Requests
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Request Overview */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Request Overview</h3>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Leave Type</h4>
                    <div className="flex items-center">
                      <div 
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: viewingRequest.LeaveType?.color || '#3B82F6' }}
                      ></div>
                      <span className="text-lg font-medium text-gray-900">
                        {viewingRequest.LeaveType?.name}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
                    <div className="flex items-center">
                      <span className="text-lg mr-2">{getStatusIcon(viewingRequest.status)}</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(viewingRequest.status)}`}>
                        {formatStatus(viewingRequest.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {getStatusDescription(viewingRequest.status)}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Start Date</h4>
                    <p className="text-lg font-medium text-gray-900">
                      {formatDate(viewingRequest.startDate)}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">End Date</h4>
                    <p className="text-lg font-medium text-gray-900">
                      {formatDate(viewingRequest.endDate)}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Total Days</h4>
                    <p className="text-lg font-medium text-gray-900">
                      {viewingRequest.totalDays} days
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Submitted</h4>
                    <p className="text-sm text-gray-900">
                      {formatDateTime(viewingRequest.createdAt)}
                    </p>
                  </div>
                </div>

                {viewingRequest.isEmergency && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                      <h4 className="text-sm font-medium text-red-800">Emergency Request</h4>
                    </div>
                    {viewingRequest.emergencyReason && (
                      <p className="mt-2 text-sm text-red-700">
                        {viewingRequest.emergencyReason}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Reason */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Reason</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-900 whitespace-pre-wrap">
                  {viewingRequest.reason}
                </p>
              </div>
            </div>

            {/* Additional Information */}
            {(viewingRequest.handoverNotes || viewingRequest.contactDuringLeave) && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Additional Information</h3>
                </div>
                <div className="px-6 py-4 space-y-4">
                  {viewingRequest.handoverNotes && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Handover Notes</h4>
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {viewingRequest.handoverNotes}
                      </p>
                    </div>
                  )}
                  
                  {viewingRequest.contactDuringLeave && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Contact During Leave</h4>
                      <p className="text-gray-900">
                        {viewingRequest.contactDuringLeave}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments */}
            {viewingRequest.attachments && viewingRequest.attachments.length > 0 && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="space-y-3">
                    {viewingRequest.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                            <p className="text-xs text-gray-500">
                              {attachment.mimetype} • {(attachment.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => window.open(`/uploads/leave-attachments/${attachment.filename}`, '_blank')}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = `/uploads/leave-attachments/${attachment.filename}`;
                              link.download = attachment.filename;
                              link.click();
                            }}
                            className="text-sm text-green-600 hover:text-green-800 font-medium"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Employee Information */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Employee</h3>
              </div>
              <div className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">
                        {viewingRequest.User?.firstName?.charAt(0)}{viewingRequest.User?.fatherName?.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {viewingRequest.User?.firstName} {viewingRequest.User?.fatherName} {viewingRequest.User?.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{viewingRequest.User?.email}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Approval Timeline */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Timeline</h3>
              </div>
              <div className="px-6 py-4">
                <div className="flow-root">
                  <ul className="-mb-8">
                    <li>
                      <div className="relative pb-8">
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center ring-8 ring-white">
                              <DocumentTextIcon className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">Request submitted</p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              {formatDateTime(viewingRequest.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                    
                    {viewingRequest.status !== 'pending' && (
                      <li>
                        <div className="relative pb-8">
                          <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                                <CheckCircleIcon className="h-5 w-5 text-white" />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                              <div>
                                <p className="text-sm text-gray-500">Request processed</p>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                {formatDateTime(viewingRequest.updatedAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            {viewingRequest.status === 'pending' && (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Actions</h3>
                </div>
                <div className="px-6 py-4 space-y-3">
                  <button
                    onClick={() => {
                      setEditingRequest(viewingRequest);
                      setCurrentView('edit-request');
                    }}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Edit Request
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {canManageAllRequests ? 'HR Leave Management' : 'Leave Management'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {canManageAllRequests 
            ? 'Manage all employee leave requests and approvals' 
            : 'Manage your leave requests and view your balance'
          }
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setCurrentView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Dashboard
          </button>
          

        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {!canManageAllRequests && (
              <button
                onClick={() => setCurrentView('new-request')}
                className="relative group bg-white p-6 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-blue-100 group-hover:bg-blue-200">
                    <PlusIcon className="h-6 w-6 text-blue-600" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900">Request New Leave</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Submit a new leave request for approval
                  </p>
                </div>
              </button>
            )}

            <button
              onClick={() => setCurrentView('requests')}
              className="relative group bg-white p-6 rounded-lg border border-gray-300 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-green-100 group-hover:bg-green-200">
                  <DocumentTextIcon className="h-6 w-6 text-green-600" />
                </span>
              </div>
              <div className="mt-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {canManageAllRequests ? 'Manage All Requests' : 'View All Requests'}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {canManageAllRequests 
                    ? 'Review and approve employee leave requests' 
                    : 'Review your leave request history'
                  }
                </p>
              </div>
            </button>

            {isManager && (
              <button
                onClick={() => setCurrentView('team-calendar')}
                className="relative group bg-white p-6 rounded-lg border border-gray-300 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-purple-100 group-hover:bg-purple-200">
                    <CalendarIcon className="h-6 w-6 text-purple-600" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900">My Team Leaves</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    View your team's leave calendar and requests
                  </p>
                  {teamSummary.totalRequests > 0 && (
                    <div className="mt-2 flex space-x-4 text-xs">
                      <span className="text-yellow-600">{teamSummary.pending} pending</span>
                      <span className="text-green-600">{teamSummary.approved} approved</span>
                      <span className="text-red-600">{teamSummary.rejected} rejected</span>
                    </div>
                  )}
                </div>
              </button>
            )}

            {canManageAllRequests && (
              <button
                onClick={() => setCurrentView('requests')}
                className="relative group bg-white p-6 rounded-lg border border-gray-300 hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <div>
                  <span className="rounded-lg inline-flex p-3 bg-yellow-100 group-hover:bg-yellow-200">
                    <UsersIcon className="h-6 w-6 text-yellow-600" />
                  </span>
                </div>
                <div className="mt-4">
                  <h3 className="text-lg font-medium text-gray-900">Pending Approvals</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Review pending leave requests that need approval
                  </p>
                </div>
              </button>
            )}
          </div>

          {/* Leave Balance */}
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Leave Balance</h2>
            {leaveBalance.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No leave balance available</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Leave balances will appear here once configured.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {leaveBalance.map((balance, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: balance.color || '#3B82F6' }}
                        >
                          <CalendarDaysIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-gray-500 truncate">
                            {balance.leaveTypeName}
                          </dt>
                          <dd className="flex items-baseline">
                            <div className="text-2xl font-semibold text-gray-900">
                              {balance.remainingDays}
                            </div>
                            <div className="ml-2 text-sm text-gray-500">
                              / {balance.totalDays} days
                            </div>
                          </dd>
                        </dl>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Requests */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">Recent Requests</h2>
              <button
                onClick={() => setCurrentView('requests')}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                View all
              </button>
            </div>

            {recentRequests.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No recent requests</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Your recent leave requests will appear here.
                </p>
              </div>
            ) : (
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {recentRequests.map((request) => (
                    <li key={request.id} className="px-6 py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div 
                              className="w-2.5 h-2.5 rounded-full"
                              style={{ backgroundColor: request.LeaveType?.color || '#3B82F6' }}
                            ></div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-gray-900">
                                {request.LeaveType?.name}
                              </p>
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                                {formatStatus(request.status)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {formatDate(request.startDate)} - {formatDate(request.endDate)} • {request.totalDays} days
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-sm text-gray-500">
                          {formatDate(request.createdAt)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
        >
          {renderTabContent()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LeaveManagement; 